import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { UserProfile } from '../types';
import { invalidateAuthTokenCache } from '../lib/authTokenCache';

// Interface que define o contexto de autenticação
export interface AuthContextType {
  user: UserProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  needsToCompleteProfile: boolean;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string; originalError?: string; isEmailConfirmationError?: boolean }>;
  signUp: (email: string, password: string, username: string, fullName: string | undefined, referralCode: string | undefined, termsAccepted: boolean, marketingConsent: boolean) => Promise<{ success: boolean; error?: string }>;
  signInWithMagicLink: (email: string) => Promise<{ success: boolean; error?: string; message?: string }>;
  signInWithGoogle: (referralCode?: string) => Promise<{ success: boolean; error?: string }>;
  linkGoogleAccount: () => Promise<{ success: boolean; error?: string }>;
  getUserIdentities: () => Promise<{ success: boolean; identities?: any[]; error?: string }>;
  signOut: () => Promise<void>;
  updateProfile: (data: Partial<UserProfile>) => Promise<{ success: boolean; error?: string }>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  resendConfirmationEmail: (email: string) => Promise<{ success: boolean; error?: string }>;
  deleteAccount: () => Promise<{ success: boolean; error?: string }>;
  completeOAuthProfileRegistration: (marketingConsent: boolean) => Promise<{ success: boolean; error?: string }>;
  generateReferralCode: () => Promise<{ success: boolean; code?: string; error?: string }>;
}

// Token armazenado em memória - NÃO usamos mais essa variável diretamente, usamos o cache no authTokenCache.ts
// A mantemos aqui apenas para compatibilidade com código existente
let authToken: string | null = null;

// Flag para controlar múltiplas verificações de autenticação
let authCheckInProgress = false;
let lastAuthCheck = 0;
const AUTH_CHECK_THROTTLE_MS = 10000; // 10 segundos entre verificações

// Criar o contexto de autenticação
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Componente que fornece o contexto de autenticação
export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Estados do contexto
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [needsToCompleteProfile, setNeedsToCompleteProfile] = useState(false);

  // Função para buscar perfil do usuário pelo ID
  const fetchUserProfile = async (userId: string) => {
    try {
      console.log("[AUTH] Buscando perfil do usuário:", userId);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error("[AUTH] Erro ao buscar perfil:", error);
        return null;
      }

      console.log("[AUTH] Perfil encontrado:", data);
      return data as UserProfile;
    } catch (error) {
      console.error("[AUTH] Erro ao buscar perfil:", error);
      return null;
    }
  };

  // Verificar autenticação ao iniciar o componente
  useEffect(() => {
    const checkAuth = async () => {
      // Evitar múltiplas verificações concorrentes
      if (authCheckInProgress) {
        console.log("[AUTH] Verificação de autenticação já em andamento, ignorando chamada duplicada");
        return;
      }
      
      // Implementar throttling para evitar muitas chamadas
      const now = Date.now();
      if (now - lastAuthCheck < AUTH_CHECK_THROTTLE_MS) {
        console.log("[AUTH] Verificação de autenticação muito frequente, ignorando");
        return;
      }
      
      try {
        authCheckInProgress = true;
        lastAuthCheck = now;
        setLoading(true);
        console.log("[AUTH] Verificando sessão...");
        
        // Tentar obter sessão atual diretamente
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("[AUTH] Erro ao verificar sessão:", sessionError);
          setLoading(false);
          authCheckInProgress = false;
          return;
        }
        
        console.log("[AUTH] Dados da sessão:", sessionData);
        
        if (sessionData?.session) {
          console.log("[AUTH] Sessão encontrada:", sessionData.session.user.id);
          
          // Para compatibilidade com código existente
          authToken = sessionData.session.access_token;
          console.log("[AUTH] Token armazenado:", authToken?.substring(0, 10) + "...");
          
          const userProfile = await fetchUserProfile(sessionData.session.user.id);
          
          if (userProfile) {
            console.log("[AUTH] Definindo usuário autenticado:", userProfile.username);
            setUser(userProfile);
            setIsAuthenticated(true);
            setIsAdmin(userProfile.is_admin || false);

            // Verificar se o perfil precisa ser completado
            if (!userProfile.terms_accepted_at) {
              console.log("[AUTH] Usuário precisa completar o perfil (termos não aceitos). User ID:", userProfile.id);
              setNeedsToCompleteProfile(true);
            } else {
              setNeedsToCompleteProfile(false);
            }
          } else {
            console.log("[AUTH] Autenticado mas sem perfil no DB, criando temporário e marcando para completar perfil");
            // Usuário está autenticado mas não tem perfil na nossa tabela profiles
            // Isso pode acontecer se o trigger de criação de perfil falhou ou para usuários muito antigos
            // Ou se é um novo usuário OAuth e o perfil ainda não foi completamente criado pela nossa lógica de signUp.
            const tempProfile: UserProfile = {
              id: sessionData.session.user.id,
              email: sessionData.session.user.email || '',
              username: sessionData.session.user.email?.split('@')[0] || 'usuario',
              is_admin: false,
              created_at: sessionData.session.user.created_at || new Date().toISOString(), // Usar created_at do Supabase Auth se disponível
              updated_at: new Date().toISOString()
              // terms_accepted_at e marketing_consent estarão undefined aqui por padrão
            };
            
            setUser(tempProfile);
            setIsAuthenticated(true);
            setIsAdmin(false);
            setNeedsToCompleteProfile(true); // Marcar para completar o perfil, pois não temos registro dos termos
            
            // Tentar criar o perfil no banco em segundo plano. A função completeOAuthProfileRegistration cuidará de setar os termos.
            supabase.from('profiles')
              .upsert({ 
                id: tempProfile.id, 
                email: tempProfile.email, 
                username: tempProfile.username, 
                is_admin: tempProfile.is_admin,
                created_at: tempProfile.created_at,
                updated_at: tempProfile.updated_at,
               }, { onConflict: 'id' })
              .then(({ error }) => {
                if (error) {
                  console.error("[AUTH] Erro ao salvar perfil temporário (que precisa de conclusão):", error);
                } else {
                  console.log("[AUTH] Perfil temporário (que precisa de conclusão) salvo no banco");
                }
              });
          }
        } else {
          console.log("[AUTH] Nenhuma sessão encontrada");
          setUser(null);
          setIsAuthenticated(false);
          setIsAdmin(false);
          setNeedsToCompleteProfile(false); // Sem sessão, não precisa completar perfil
        }
      } catch (error) {
        console.error("[AUTH] Erro inesperado ao verificar autenticação:", error);
      } finally {
        setLoading(false);
        authCheckInProgress = false;
      }
    };
    
    checkAuth();
    
    // Adicionar listener para mudanças de autenticação
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("[AUTH] Estado de autenticação alterado:", event);
      
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        // Não precisamos fazer nada aqui, pois o checkAuth já será chamado em caso de login
        // E não queremos duplicar o trabalho
        if (session) {
          authToken = session.access_token;
        }
      } else if (event === 'SIGNED_OUT') {
        // Limpar o estado em caso de logout
        setUser(null);
        setIsAuthenticated(false);
        setIsAdmin(false);
        setNeedsToCompleteProfile(false); // Logout, não precisa completar perfil
        authToken = null;
        invalidateAuthTokenCache();
      }
    });
    
    // Limpar o ouvinte ao desmontar o componente
    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  // Função para realizar login
  const signIn = async (email: string, password: string) => {
    try {
      console.log("[AUTH] Tentando fazer login com:", email);
      
      // Login direto
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      console.log("[AUTH] Resposta do login:", data?.session ? "Sucesso" : "Falha", error);
      
      if (error) {
        console.error("[AUTH] Erro de login:", error);
        
        // Verificar explicitamente erros de e-mail não confirmado
        if (error.message && (
            error.message.includes("Email not confirmed") || 
            error.message.includes("Invalid login credentials") ||
            error.message.includes("Email não confirmado") ||
            error.message.includes("credenciais")
           )) {
          return { 
            success: false, 
            error: "Por favor, confirme seu e-mail antes de fazer login. Verifique sua caixa de entrada e spam para o link de confirmação.",
            originalError: error.message,
            isEmailConfirmationError: true
          };
        }
        
        return { success: false, error: error.message };
      }
      
      if (data?.session) {
        // Para compatibilidade com código existente
        authToken = data.session.access_token;
        console.log("[AUTH] Token de login armazenado:", authToken?.substring(0, 10) + "...");
        
        // Configurar um timeout para a busca do perfil
        const profilePromise = fetchUserProfile(data.user.id);
        
        // Promise com timeout de 5 segundos
        const timeoutPromise = new Promise<null>((resolve) => {
          setTimeout(() => resolve(null), 5000);
        });
        
        // Usar o perfil que chegar primeiro ou o timeout
        const userProfile = await Promise.race([profilePromise, timeoutPromise]);
        
        if (userProfile) {
          console.log("[AUTH] Usando perfil do banco de dados:", userProfile.username);
          setUser(userProfile);
          setIsAuthenticated(true);
          setIsAdmin(userProfile.is_admin || false);

          // ADICIONANDO LÓGICA PARA needsToCompleteProfile AQUI
          if (!userProfile.terms_accepted_at) {
            console.log("[AUTH - signIn] Usuário precisa completar o perfil (termos não aceitos). User ID:", userProfile.id);
            setNeedsToCompleteProfile(true);
          } else {
            console.log("[AUTH - signIn] Usuário NÃO precisa completar o perfil (termos aceitos). User ID:", userProfile.id);
            setNeedsToCompleteProfile(false);
          }

        } else {
          console.log("[AUTH] Criando perfil temporário em memória");
          // Criar um perfil padrão em memória se não conseguir buscar do banco
          const tempProfile: UserProfile = {
            id: data.user.id,
            email: data.user.email || email,
            username: email.split('@')[0],
            is_admin: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          
          setUser(tempProfile);
          setIsAuthenticated(true);
          setIsAdmin(false);
          
          // Se o perfil é temporário, assumimos que precisa completar.
          console.log("[AUTH - signIn] Perfil temporário, usuário precisa completar o perfil.");
          setNeedsToCompleteProfile(true);
          
          // Tentar criar o perfil no banco em segundo plano
          supabase.from('profiles')
            .upsert(tempProfile, { onConflict: 'id' })
            .then(({ error }) => {
              if (error) {
                console.error("[AUTH] Erro ao salvar perfil temporário:", error);
              } else {
                console.log("[AUTH] Perfil temporário salvo no banco");
              }
            });
        }
        
        return { success: true };
      }
      
      return { success: false, error: "Falha ao autenticar. Tente novamente." };
    } catch (error: any) {
      console.error('[AUTH] Erro ao entrar:', error);
      return { success: false, error: error.message || 'Erro ao entrar' };
    }
  };

  // Função para criar uma nova conta de usuário
  const signUp = async (
    email: string,
    password: string,
    username: string,
    fullName?: string,
    referralCode?: string,
    termsAccepted?: boolean, // Marcar como opcional aqui para manter a compatibilidade, mas AuthForm garantirá que seja true
    marketingConsent?: boolean
  ) => {
    try {
      console.log("[AUTH] Tentando criar conta para:", email, "Username:", username, "Referral:", referralCode);
      
      // Primeiro, criar o usuário no Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username: username,
            full_name: fullName,
            referral_code_signup_attempt: referralCode,
            terms_accepted_signup_attempt: termsAccepted,
            marketing_consent_signup_attempt: marketingConsent,
          },
        },
      });

      if (authError) {
        console.error("[AUTH] Erro ao criar usuário (Auth):", authError);
        // Verificar se o erro é de usuário já existente
        if (authError.message.includes("User already registered") || authError.message.includes("already registered")) {
          return { success: false, error: "Este email já está cadastrado. Tente fazer login." };
        }
        if (authError.message.includes("Unable to validate email address") || authError.message.includes("Invalid email")) {
          return { success: false, error: "Endereço de email inválido." };
        }
        if (authError.message.includes("Password should be at least 6 characters")) {
          return { success: false, error: "A senha deve ter pelo menos 6 caracteres." };
        }
        // Outros erros de validação do Supabase (ex: formato do username) podem ser tratados aqui
        return { success: false, error: authError.message };
      }
      
      if (authData.user) {
        console.log("[AUTH] Usuário criado no Auth e aguardando confirmação:", authData.user.id);
        // Email de confirmação será enviado automaticamente pelo Supabase Auth.
        // A criação do perfil em public.profiles agora será responsabilidade
        // de um trigger no backend após a confirmação do email.
        return { success: true, error: undefined }; // Modificado para indicar sucesso e necessidade de confirmação
      } else if (authData.session && !authData.user) {
        // Este caso pode ocorrer se o usuário já existe e está confirmado,
        // mas tentou se cadastrar novamente. O Supabase retorna a sessão.
        console.warn("[AUTH] Tentativa de cadastro para usuário já existente e confirmado:", email);
        return { success: false, error: "Este email já está cadastrado e confirmado. Tente fazer login." };
        }

        
      console.warn("[AUTH] Situação inesperada no signUp, sem usuário e sem erro claro:", authData);
      return { success: false, error: "Ocorreu um erro desconhecido ao criar a conta." };

    } catch (error: any) {
      console.error("[AUTH] Erro inesperado no signUp:", error);
      return { success: false, error: error.message || "Ocorreu um erro ao criar a conta." };
    }
  };

  // Função para enviar magic link
  const signInWithMagicLink = async (email: string) => {
    try {
      console.log("[AUTH] Enviando magic link para:", email);
      const { data, error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: window.location.origin,
        },
      });
      
      console.log("[AUTH] Resultado do magic link:", data ? "Enviado" : "Falha", error);
      
      if (error) {
        console.error("[AUTH] Erro ao enviar magic link:", error);
        return { success: false, error: error.message };
      }
      
      return { 
        success: true, 
        message: 'Enviamos um link de acesso para seu email. Por favor, verifique sua caixa de entrada.' 
      };
    } catch (error: any) {
      console.error('[AUTH] Erro ao enviar magic link:', error);
      return { success: false, error: error.message || 'Erro ao enviar link de acesso' };
    }
  };

  // Função para fazer login com Google
  const signInWithGoogle = async (referralCode?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("[AUTH] Tentando fazer login com Google. Código de referência recebido:", referralCode);
      
      const oauthOptions: { redirectTo: string; queryParams: any; data?: { [key: string]: any } } = {
          redirectTo: window.location.origin,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
      };

      if (referralCode && referralCode.trim() !== '') {
        oauthOptions.data = { referral_code_signup_attempt: referralCode };
        console.log("[AUTH] Passando referral_code_signup_attempt para o OAuth:", referralCode);
        }

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: oauthOptions // Usar o objeto oauthOptions modificado
      });
      
      console.log("[AUTH] Resposta do login com Google:", data ? "Iniciando fluxo" : "Falha", error);
      
      if (error) {
        console.error("[AUTH] Erro de login com Google:", error);
        return { success: false, error: error.message };
      }
      
      // O retorno dessa função é diferente dos outros métodos de login
      // pois o signInWithOAuth redireciona o usuário e não retorna os dados de sessão imediatamente
      return { success: true };
      
    } catch (error: any) {
      console.error("[AUTH] Erro inesperado ao fazer login com Google:", error);
      return { success: false, error: error.message || "Erro ao fazer login com Google" };
    }
  };

  // Função para vincular conta Google a uma conta existente
  const linkGoogleAccount = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("[AUTH] Tentando vincular conta Google");
      
      if (!isAuthenticated) {
        console.error("[AUTH] Usuário precisa estar autenticado para vincular contas");
        return { success: false, error: "Você precisa estar autenticado para vincular sua conta Google" };
      }
      
      const { data, error } = await supabase.auth.linkIdentity({
        provider: 'google',
        options: {
          redirectTo: 'https://boardverso.com/auth/v1/callback'
        }
      });
      
      console.log("[AUTH] Resposta da vinculação com Google:", data ? "Iniciando fluxo" : "Falha", error);
      
      if (error) {
        console.error("[AUTH] Erro ao vincular com Google:", error);
        return { success: false, error: error.message };
      }
      
      // A função linkIdentity redireciona o usuário e não retorna os dados imediatamente
      return { success: true };
      
    } catch (error: any) {
      console.error("[AUTH] Erro inesperado ao vincular conta Google:", error);
      return { success: false, error: error.message || "Erro ao vincular conta Google" };
    }
  };

  // Função para obter as identidades vinculadas ao usuário atual
  const getUserIdentities = async (): Promise<{ success: boolean; identities?: any[]; error?: string }> => {
    try {
      console.log("[AUTH] Buscando identidades vinculadas do usuário");
      
      if (!isAuthenticated) {
        console.error("[AUTH] Usuário não está autenticado");
        return { success: false, error: "Usuário não está autenticado" };
      }
      
      const { data, error } = await supabase.auth.getUserIdentities();
      
      if (error) {
        console.error("[AUTH] Erro ao buscar identidades:", error);
        return { success: false, error: error.message };
      }
      
      const identities = data?.identities || [];
      console.log("[AUTH] Identidades encontradas:", identities.length);
      
      return { success: true, identities };
      
    } catch (error: any) {
      console.error("[AUTH] Erro inesperado ao buscar identidades:", error);
      return { success: false, error: error.message || "Erro ao buscar identidades vinculadas" };
    }
  };

  // Função para fazer logout
  const signOut = async () => {
    try {
      console.log("[AUTH] Iniciando logout");
      // Fazer logout no Supabase
      await supabase.auth.signOut();
      
      // Limpar token e cache
      authToken = null;
      invalidateAuthTokenCache();
      
      // Limpar estado
      setUser(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
      
      console.log("[AUTH] Logout concluído");
    } catch (error) {
      console.error('[AUTH] Erro ao sair:', error);
      throw error;
    }
  };

  // Função para atualizar perfil do usuário
  const updateProfile = async (data: Partial<UserProfile>) => {
    if (!user) {
      return { success: false, error: "Usuário não autenticado." };
      }

    // Não permitir atualização do ID ou email diretamente por esta função para segurança
    const { id, email, ...dataToUpdate } = data;

    // Log para verificar o que está sendo enviado para atualização
    console.log("[AUTH] Atualizando perfil para o usuário:", user.id, "com dados:", dataToUpdate);

    try {
      const { error } = await supabase
        .from('profiles')
        .update(dataToUpdate) // Usar dataToUpdate que não contém id nem email
        .eq('id', user.id);

      if (error) {
        console.error("[AUTH] Erro ao atualizar perfil:", error);
        return { success: false, error: error.message };
      }

      // Atualizar o estado local do usuário com os novos dados
      // É importante pegar todos os dados do usuário e mesclar com os atualizados
      const updatedUserProfile = { ...user, ...dataToUpdate } as UserProfile;
      setUser(updatedUserProfile);
      setIsAdmin(updatedUserProfile.is_admin || false);
      
      console.log("[AUTH] Perfil atualizado com sucesso para:", user.id, "Novos dados:", updatedUserProfile);
      return { success: true };
    } catch (error: any) {
      console.error('[AUTH] Erro ao atualizar perfil:', error);
      return { success: false, error: error.message || 'Erro ao atualizar perfil' };
    }
  };

  // Função para atualizar senha
  const updatePassword = async (currentPassword: string, newPassword: string) => {
    try {
      console.log("[AUTH] Tentando atualizar senha");
      
      // Primeiro, reautenticar o usuário com a senha atual
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: currentPassword
      });
      
      if (authError) {
        console.error("[AUTH] Erro na autenticação durante atualização de senha:", authError);
        return { success: false, error: "Senha atual incorreta" };
      }
      
      // Usuário autenticado, atualizar a senha
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) {
        console.error("[AUTH] Erro ao atualizar senha:", error);
        return { success: false, error: error.message };
      }
      
      console.log("[AUTH] Senha atualizada com sucesso");
      return { success: true };
    } catch (error: any) {
      console.error("[AUTH] Erro inesperado ao atualizar senha:", error);
      return { success: false, error: error.message || "Erro ao atualizar senha" };
    }
  };

  // Função para reenviar e-mail de confirmação
  const resendConfirmationEmail = async (email: string) => {
    try {
      console.log("[AUTH] Tentando reenviar e-mail de confirmação para:", email);
      
      const { data, error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: window.location.origin,
        }
      });
      
      console.log("[AUTH] Resultado do reenvio:", data ? "Enviado" : "Falha", error);
      
      if (error) {
        console.error("[AUTH] Erro ao reenviar e-mail:", error);
        return { success: false, error: error.message };
      }
      
      return { 
        success: true, 
        message: 'E-mail de confirmação reenviado com sucesso. Por favor, verifique sua caixa de entrada.' 
      };
    } catch (error: any) {
      console.error('[AUTH] Erro ao reenviar e-mail de confirmação:', error);
      return { success: false, error: error.message || 'Erro ao reenviar e-mail de confirmação' };
    }
  };

  // Função para deletar conta do usuário
  const deleteAccount = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("[AUTH] Iniciando processo de exclusão de conta");
      
      if (!isAuthenticated || !user) {
        console.error("[AUTH] Usuário não está autenticado");
        return { success: false, error: "Usuário não está autenticado" };
      }
      
      // 1. Deletar os dados do usuário da tabela profiles
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', user.id);
      
      if (profileError) {
        console.error("[AUTH] Erro ao deletar perfil:", profileError);
        return { success: false, error: profileError.message };
      }
      
      // 2. Deletar o usuário da autenticação do Supabase
      const { error: authError } = await supabase.auth.admin.deleteUser(user.id);
      
      if (authError) {
        console.error("[AUTH] Erro ao deletar usuário:", authError);
        return { success: false, error: authError.message };
      }
      
      // 3. Fazer logout após a exclusão
      await signOut();
      
      console.log("[AUTH] Conta deletada com sucesso");
      return { success: true };
    } catch (error: any) {
      console.error("[AUTH] Erro ao deletar conta:", error);
      return { success: false, error: error.message || "Erro ao deletar conta" };
    }
  };

  // Função para completar o registro de perfil OAuth (ou qualquer perfil que precise)
  const completeOAuthProfileRegistration = async (marketingConsent: boolean): Promise<{ success: boolean; error?: string }> => {
    if (!user) {
      return { success: false, error: "Usuário não autenticado." };
    }
    console.log(`[AUTH] Chamando completeOAuthProfileRegistration para usuário ${user.id} com marketingConsent: ${marketingConsent}`);

    const profileUpdateData: Partial<UserProfile> & { terms_accepted_at: string } = {
      terms_accepted_at: new Date().toISOString(),
      marketing_consent: marketingConsent,
      updated_at: new Date().toISOString(), // Garantir que updated_at seja atualizado
    };

    try {
      const { error } = await supabase
        .from('profiles')
        .update(profileUpdateData)
        .eq('id', user.id);

      if (error) {
        console.error("[AUTH] Erro ao atualizar perfil para completar registro:", error);
        return { success: false, error: error.message };
      }

      // Atualizar o estado local do usuário para refletir as mudanças
      const updatedLocalProfile = { 
        ...user,
        terms_accepted_at: profileUpdateData.terms_accepted_at,
        marketing_consent: profileUpdateData.marketing_consent,
        updated_at: profileUpdateData.updated_at
      } as UserProfile;
      setUser(updatedLocalProfile);
      
      setNeedsToCompleteProfile(false); // Muito importante: sinalizar que o perfil está completo
      console.log("[AUTH] Registro de perfil completado com sucesso para:", user.id);
      return { success: true };

    } catch (error: any) {
      console.error("[AUTH] Erro inesperado em completeOAuthProfileRegistration:", error);
      return { success: false, error: error.message || "Erro ao completar registro do perfil." };
    }
  };

  const generateReferralCode = async (): Promise<{ success: boolean; code?: string; error?: string }> => {
    if (!user) {
      return { success: false, error: "Usuário não autenticado." };
    }

    if (user.referral_code) {
        console.log("[AUTH] Usuário já possui código de referência no estado local:", user.referral_code);
        return { success: true, code: user.referral_code };
    }

    try {
      console.log("[AUTH] Tentando gerar/obter código de referência para o usuário:", user.id);
      // Usar 'as any' para o nome da função RPC para contornar o problema de tipagem temporariamente
      const { data: rpcData, error: rpcError } = await supabase.rpc('generate_and_set_user_referral_code' as any, {
        user_id_param: user.id,
      });

      if (rpcError) {
        console.error("[AUTH] Erro ao gerar/obter código de referência via RPC:", rpcError);
        let userFriendlyError = rpcError.message;
        if (rpcError.message.includes('USR_REF_CODE_GEN_FAILED')) {
            userFriendlyError = 'Não foi possível gerar um código de indicação único no momento. Por favor, tente novamente mais tarde.';
        } else if (rpcError.message.includes('USR_PROFILE_NOT_FOUND')) {
            userFriendlyError = 'Perfil do usuário não encontrado. Contate o suporte.';
        }
        return { success: false, error: userFriendlyError };
      }

      // A função SQL retorna TEXT, então esperamos que rpcData seja uma string.
      // Adicionar uma verificação de tipo para segurança.
      if (typeof rpcData === 'string' && rpcData) {
        console.log("[AUTH] Código de referência obtido/gerado e salvo:", rpcData);
        const updatedUserProfile = { ...user, referral_code: rpcData, updated_at: new Date().toISOString() } as UserProfile;
        setUser(updatedUserProfile);
        return { success: true, code: rpcData };
      } else {
        console.error("[AUTH] Resposta inesperada da RPC. Esperava uma string, recebeu:", rpcData);
        return { success: false, error: "Resposta inesperada ao obter o código de referência." };
      }

    } catch (error: any) {
      console.error('[AUTH] Erro inesperado ao gerar/obter código de referência:', error);
      return { success: false, error: error.message || 'Erro inesperado ao gerar/obter código de referência.' };
    }
  };

  // Criar objeto de valor do contexto usando useMemo para estabilidade
  const authContextValue = React.useMemo(() => ({
    user,
    loading,
    isAuthenticated,
    isAdmin,
    needsToCompleteProfile,
    signIn,
    signUp,
    signInWithMagicLink,
    signInWithGoogle,
    linkGoogleAccount,
    getUserIdentities,
    signOut,
    updateProfile,
    updatePassword,
    resendConfirmationEmail,
    deleteAccount,
    completeOAuthProfileRegistration,
    generateReferralCode,
  }), [user, loading, isAuthenticated, isAdmin, needsToCompleteProfile]);

  // Renderizar o provedor de contexto
  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook para acessar o contexto de autenticação
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}